import React, { useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { Dropdown, Button, Drawer, Badge, Tooltip, notification, Alert } from 'antd'
import type { MenuProps } from 'antd'
import { notificationStore, isUnread, isForUser } from '@/pages/Notification/notificationStore'
import { authStore } from '@/context/auth'
import { notifySuccess } from '@/utils/notify'
import {
  DashboardOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  MailOutlined,
  FileProtectOutlined,
  SwapOutlined,
  CopyOutlined,
  CalculatorOutlined,
  DollarOutlined,
  FileExcelOutlined,
  CreditCardOutlined,
  ShoppingOutlined,
  UnorderedListOutlined,
  SettingOutlined,
  AppstoreOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  DownOutlined,
  ThunderboltOutlined,
  TeamOutlined,
  PhoneOutlined,
  BarChartOutlined,
  BulbOutlined,
  ApartmentOutlined,
  BankOutlined,
  EnvironmentOutlined,
  FieldTimeOutlined,
  TagOutlined,
  TagsOutlined,
  WhatsAppOutlined,
  DatabaseOutlined,
  NumberOutlined,
} from '@ant-design/icons'
import logo from '@/assests/Shakthi Power outline logo.png'
import { useMasterData } from '@/context/MasterDataContext'
import { hasModulePermission, type ModuleName } from '@/utils/permissions'
import './MainLayout.css'

/* ─────────────────────────────────────────────────────────
   Sidebar nav definitions
───────────────────────────────────────────────────────── */
interface SectionChild {
  key: string
  label: string
  icon?: React.ReactNode
  // Role Master module that governs this item's visibility. Omitted for
  // items every signed-in user can see regardless of permissions.
  module?: ModuleName
}

interface SidebarSection {
  key: string
  label: string
  icon: React.ReactNode
  path?: string
  children?: SectionChild[]
}

const sidebarSections: SidebarSection[] = [
  { key: '/', label: 'Overview', icon: <DashboardOutlined />, path: '/' },
  {
    key: 'sales',
    label: 'Sales',
    icon: <FileTextOutlined />,
    children: [
      { key: '/sales/dashboard', label: 'Sales Dashboard',     icon: <DashboardOutlined />, module: 'Leads' },
      { key: '/leads',           label: 'Lead',                icon: <FileTextOutlined />, module: 'Leads' },
      { key: '/proposals',       label: 'Proposal',            icon: <FilePdfOutlined />, module: 'Proposals' },
      { key: '/agreements',      label: 'Agreement',           icon: <FileProtectOutlined />, module: 'Agreements' },
      { key: '/dcr',             label: 'Daily Call Report',   icon: <PhoneOutlined />, module: 'DCR' },
    ],
  },
  {
    key: 'generator',
    label: 'Generator',
    icon: <ThunderboltOutlined />,
    children: [
      { key: '/master/generators', label: 'Generator', icon: <ThunderboltOutlined />, module: 'Generator' },
      { key: '/master/customers',  label: 'Consumer',  icon: <TeamOutlined />, module: 'Consumer' },
      { key: '/mappings',          label: 'Generator-Consumer Mapping', icon: <SwapOutlined />, module: 'Mapping' },
    ],
  },
  {
    key: 'operation',
    label: 'Allocations',
    icon: <SwapOutlined />,
    children: [
      { key: '/allocations/dashboard', label: 'Allocation Dashboard', icon: <DashboardOutlined />, module: 'Allocations' },
      { key: '/meter-readings',        label: 'Meter Readings',       icon: <CopyOutlined />, module: 'Allocations' },
      { key: '/allocations',           label: 'Unit Allocation',      icon: <CalculatorOutlined />, module: 'Allocations' },
    ],
  },
  {
    key: 'financial',
    label: 'Financial',
    icon: <DollarOutlined />,
    children: [
      { key: '/financial/dashboard', label: 'Finance Dashboard', icon: <DashboardOutlined />, module: 'Finance' },
      { key: '/invoices',            label: 'Invoice',           icon: <FileExcelOutlined />, module: 'Finance' },
      { key: '/payments',            label: 'Payments',          icon: <CreditCardOutlined />, module: 'Finance' },
      // { key: '/payouts',             label: 'Payout',            icon: <ShoppingOutlined />, module: 'Finance' },
    ],
  },
  { key: '/calculator', label: 'Savings Calculator', icon: <CalculatorOutlined />, path: '/calculator' },
]

/* ─────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────── */
const MainLayout: React.FC = () => {
  const navigate     = useNavigate()
  const location     = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  // Sidebar collapsed → icons only (session-only, not persisted).
  const [collapsed, setCollapsed] = useState<boolean>(false)

  const toggleCollapsed = () => setCollapsed(prev => !prev)

  const currentUser = authStore.getUser()
  const userInitial = (currentUser?.name || 'U').charAt(0).toUpperCase()
  const firstName   = (currentUser?.name || 'User').split(' ')[0]

  // Profile completion banner state
  const { employees, roles } = useMasterData()

  // Only show sidebar items / the Masters config panel for modules the
  // signed-in user's role is actually permitted to view — previously every
  // item was shown to every user regardless of their role's permissions.
  const canView = (mod: ModuleName) => hasModulePermission(currentUser, roles, mod, 'view')
  const visibleSidebarSections = sidebarSections
    .map((section) => {
      if (!section.children) return section
      const children = section.children.filter((c) => !c.module || canView(c.module))
      return { ...section, children }
    })
    .filter((section) => !section.children || section.children.length > 0)
  const canViewMasters = canView('Masters')
  const [bannerDismissed, setBannerDismissed] = useState(
    () => !!sessionStorage.getItem(`sp_profile_done_${currentUser?.email || ''}`)
  )
  const empRecord = currentUser
    ? employees.find(e => (e.email || '').toLowerCase() === currentUser.email.toLowerCase())
    : undefined
  const profilePct = (() => {
    if (!empRecord) return 0
    const filled = [
      !!(empRecord.personalMobile || empRecord.dateOfBirth || empRecord.bloodGroup),
      !!(empRecord.aadhaarNumber || empRecord.panNumber),
      !!(empRecord.bankName || empRecord.accountNumber),
    ].filter(Boolean).length
    return Math.round((filled / 3) * 100)
  })()
  const showProfileBanner =
    !bannerDismissed &&
    currentUser?.role === 'employee' &&
    profilePct < 100

  useEffect(() => {
    // Only count / pop notifications actually addressed to this user — same
    // rule the Notifications page uses (isForUser), so the bell badge can never
    // advertise a notification the page then refuses to show.
    const isAdmin = authStore.isAdmin()
    const mine = () =>
      notificationStore.getAll().filter(n => isForUser(n, currentUser, isAdmin))

    setUnreadCount(mine().filter(n => isUnread(n.status)).length)

    const handleStoreUpdate = (e: Event) => {
      const customEvent = e as CustomEvent
      const { item, isNew } = customEvent.detail || {}
      setUnreadCount(mine().filter(n => isUnread(n.status)).length)
      if (isNew && item && isForUser(item, currentUser, isAdmin)) {
        const msgParts = (item.message || '').split('|||LEAD_ID:')
        const displayMsg = msgParts[0]
        const actionLeadId = msgParts[1]

        notification.open({
          key: item.id || `notif-${Date.now()}`,
          message: <div style={{ fontSize: 14, fontWeight: 600, color: '#0B1426', display: 'flex', alignItems: 'center', gap: 8 }}>
            <BellOutlined style={{ color: '#F59E0B' }} /> {item.title}
          </div>,
          description: (
            <div style={{ paddingLeft: 22 }}>
              <p style={{ margin: '4px 0 12px', fontSize: 13, color: '#4A5568', lineHeight: 1.5 }}>{displayMsg}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <span style={{ fontSize: 10, background: '#FEF3C7', color: '#B45309', padding: '2px 6px', borderRadius: 4, fontWeight: 600, textTransform: 'uppercase' }}>
                    {(item.eventTrigger || 'SYSTEM_ALERT').replace(/_/g, ' ')}
                  </span>
                </div>
                <Button type="link" size="small" style={{ padding: 0, fontSize: 12 }} onClick={() => navigate(actionLeadId ? `/leads/${actionLeadId}` : '/notifications')}>
                  {actionLeadId ? 'View Lead' : 'View Details'}
                </Button>
              </div>
            </div>
          ),
          placement: 'topRight',
          duration: 8,
          style: {
            width: 380,
            borderRadius: 8,
            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)',
            border: '1px solid #E5E7EB',
            borderLeft: '4px solid #F59E0B',
            padding: '16px 20px',
          },
          closeIcon: <span style={{ fontSize: 16, color: '#9CA3AF' }}>×</span>
        })
      }
    }

    window.addEventListener(notificationStore.eventName, handleStoreUpdate)
    return () => window.removeEventListener(notificationStore.eventName, handleStoreUpdate)
    // Re-subscribe if the signed-in user changes — the closure above captures
    // who "mine" means, so a stale one would keep counting the old user's rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.name, currentUser?.role])

  const isGroupExpanded = (section: SidebarSection) =>
    expandedGroups.has(section.key) ||
    (section.children?.some(c => location.pathname === c.key || location.pathname.startsWith(c.key + '/')) ?? false)

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  /* Masters gear dropdown */
  const masterItems = [
    { key: '/master/code-masters',        label: 'Code Master',          icon: <NumberOutlined /> },
    { key: '/master/roles',              label: 'Role Master',         icon: <SettingOutlined /> },
    { key: '/master/workflow',           label: 'Workflow Master',     icon: <ApartmentOutlined /> },
    { key: '/master/users',              label: 'Employee Master',    icon: <TeamOutlined /> },
    { key: '/master/tariffs',            label: 'Sakthi Power Tariff', icon: <ThunderboltOutlined /> },
    { key: '/master/tneb-categories',    label: 'TNEB Category',       icon: <TagsOutlined /> },
    { key: '/master/tneb-tariffs',       label: 'TNEB Tariff',         icon: <BankOutlined /> },
    { key: '/master/gst',                label: 'GST Master',          icon: <CalculatorOutlined /> },
    { key: '/master/slots',              label: 'Slot Master',         icon: <FieldTimeOutlined /> },
    { key: '/master/power-types',        label: 'Type of Power',       icon: <BulbOutlined /> },
    { key: '/master/supply-modes',       label: 'Mode of Supply',      icon: <SwapOutlined /> },
    { key: '/master/edcs',               label: 'EDC Master',          icon: <EnvironmentOutlined /> },
    { key: '/master/injecting-voltages', label: 'Injecting Voltage',   icon: <DashboardOutlined /> },
    { key: '/master/drawal-voltages',    label: 'Drawal Voltage',      icon: <DashboardOutlined /> },
    { key: '/master/agreement-types',    label: 'Agreement Types',     icon: <TagOutlined /> },
    { key: '/master/bank-accounts',      label: 'Bank Master',         icon: <BankOutlined /> },
    { key: '/master/proposal-templates',  label: 'Proposal Templates',   icon: <FilePdfOutlined /> },
    { key: '/master/email-templates',     label: 'Email Templates',      icon: <MailOutlined /> },
    { key: '/master/whatsapp-templates',  label: 'WhatsApp Templates',   icon: <WhatsAppOutlined /> },
    // Whole-database backup — admin-only, both here (so non-admins never see
    // the entry) and on the backend route (so it can't be reached directly
    // by URL either).
    ...(authStore.isAdmin() ? [{ key: '/master/backup', label: 'Database Backup', icon: <DatabaseOutlined /> }] : []),
  ]

  const mastersDropdownPanel = (
    <div className="ml-masters-panel">
      <div className="ml-masters-panel-header">
        <SettingOutlined className="ml-masters-panel-header-icon" />
        <span>Configuration</span>
      </div>
      <div className="ml-masters-panel-grid">
        {masterItems.map(item => (
          <button
            key={item.key}
            className={`ml-masters-item${location.pathname.startsWith(item.key) ? ' active' : ''}`}
            onClick={() => navigate(item.key)}
          >
            <span className="ml-masters-item-icon">{item.icon}</span>
            <span className="ml-masters-item-label">{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  )

  /* User dropdown */
  const userMenu: MenuProps['items'] = [
    {
      key: 'head',
      label: (
        <div className="ml-user-head">
          <div className="ml-user-avatar-lg">{userInitial}</div>
          <div>
            <div className="ml-user-name-lg">{currentUser?.name}</div>
            <div className="ml-user-email">{currentUser?.email}</div>
          </div>
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' },
    { key: 'profile',  icon: <UserOutlined />,    label: 'My Profile',  onClick: () => navigate('/profile') },
    { key: 'reports',  icon: <BarChartOutlined />, label: 'Reports',     onClick: () => navigate('/reports/lead-conversion') },
    { key: 'switch-software', icon: <AppstoreOutlined />, label: 'Switch Software', onClick: () => navigate('/select-software') },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: <span className="ml-logout-label">Sign Out</span>,
      onClick: () => {
        authStore.clear()
        notifySuccess('Signed out', 'You have been logged out successfully')
        navigate('/login')
      },
    },
  ]

  /* Render one sidebar section — expandable group (children nested inline) */
  const renderSidebarSection = (section: SidebarSection) => {
    const hasChildren = !!section.children?.length
    const isActive = location.pathname === section.path ||
      (section.children?.some(c => location.pathname === c.key || location.pathname.startsWith(c.key + '/')) ?? false)
    const expanded = !collapsed && hasChildren && isGroupExpanded(section)

    const header = (
      <button
        className={`ml-sb-item${isActive ? ' active' : ''}`}
        onClick={() => {
          if (section.path) navigate(section.path)
          else if (!hasChildren) return
          else if (collapsed) navigate(section.children![0].key)  // icons-only: jump to first child
          else toggleGroup(section.key)                            // expanded: open/close the group
        }}
      >
        <span className="ml-sb-icon">{section.icon}</span>
        <span className="ml-sb-label">{section.label}</span>
        {hasChildren && (
          <span className={`ml-sb-caret${expanded ? ' open' : ''}`}><DownOutlined /></span>
        )}
      </button>
    )

    return (
      <div key={section.key} className="ml-sb-group">
        {collapsed
          ? <Tooltip title={section.label} placement="right">{header}</Tooltip>
          : header}

        {expanded && (
          <div className="ml-sb-children">
            {section.children!.map(child => {
              const childActive = location.pathname === child.key || location.pathname.startsWith(child.key + '/')
              return (
                <button
                  key={child.key}
                  className={`ml-sb-subitem${childActive ? ' active' : ''}`}
                  onClick={() => navigate(child.key)}
                >
                  <span className="ml-sb-icon">{child.icon}</span>
                  <span className="ml-sb-label">{child.label}</span>
                </button>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="ml-root">

      {/* ══ LEFT SIDEBAR ═════════════════════════════════════ */}
      <aside className={`ml-sidebar${collapsed ? ' ml-sidebar--collapsed' : ''}`}>
        <div className="ml-sidebar-logo" onClick={() => navigate('/')}>
          <img src={logo} alt="Shakthi Power" className="ml-sidebar-logo-img" />
        </div>
        <nav className="ml-sidebar-nav">
          {visibleSidebarSections.map(renderSidebarSection)}
        </nav>
        <button
          className="ml-sb-collapse ml-sb-logout"
          onClick={() => {
            authStore.clear()
            notifySuccess('Signed out', 'You have been logged out successfully')
            navigate('/login')
          }}
          title="Sign Out"
        >
          <LogoutOutlined />
          <span className="ml-sb-label">Sign Out</span>
        </button>
        <button
          className="ml-sb-collapse"
          onClick={toggleCollapsed}
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          <span className="ml-sb-label">Collapse</span>
        </button>
      </aside>

      {/* ══ MAIN COLUMN ══════════════════════════════════════ */}
      <div className="ml-main-col">

        {/* ══ SLIM TOPBAR ════════════════════════════════════ */}
        <header className="ml-topbar">
          <div className="ml-topbar-inner">
            {/* Mobile hamburger */}
            <button className="ml-hamburger" onClick={() => setMobileOpen(true)}>
              <MenuOutlined />
            </button>

            <div style={{ flex: 1 }} />

            <div className="ml-topbar-right">
              {/* Notifications bell */}
              <button
                className="ml-bell-btn"
                onClick={() => navigate('/notifications')}
                title="Notifications"
              >
                <Badge count={unreadCount} size="small" offset={[-2, 2]}>
                  <BellOutlined className="ml-bell-icon" />
                </Badge>
              </button>

              {/* Masters gear icon — hidden entirely if the user's role has no
                  Masters view permission (all master screens live under one
                  Role Master permission group, so it's all-or-nothing). */}
              {canViewMasters && (
                <Dropdown
                  dropdownRender={() => mastersDropdownPanel}
                  trigger={['click']}
                  placement="bottomRight"
                >
                  <button className="ml-gear-btn" title="Configuration">
                    <SettingOutlined className="ml-gear-icon" />
                  </button>
                </Dropdown>
              )}

              {/* User */}
              <Dropdown
                menu={{ items: userMenu }}
                trigger={['click']}
                overlayClassName="ml-user-dropdown"
              >
                <button className="ml-user-chip">
                  <div className="ml-user-avatar">{userInitial}</div>
                  <span className="ml-user-chip-name">{firstName}</span>
                  <DownOutlined className="ml-user-chip-arrow" />
                </button>
              </Dropdown>
            </div>
          </div>
        </header>

        {/* ══ PROFILE COMPLETION BANNER ══════════════════════ */}
        {showProfileBanner && (
          <div style={{ padding: '12px 24px 0' }}>
            <Alert
              type="warning"
              showIcon
              style={{ borderRadius: 10 }}
              message={
                <span>
                  <strong>Complete your profile</strong>
                  <span style={{ fontWeight: 400, marginLeft: 8, color: '#78350f' }}>
                    — {profilePct}% done. Fill in your personal, statutory, and bank details.
                  </span>
                </span>
              }
              action={
                <Button
                  size="small"
                  type="primary"
                  style={{ background: '#d97706', border: 'none' }}
                  onClick={() => navigate('/profile', { state: { tab: 'personal' } })}
                >
                  Complete Now
                </Button>
              }
              closable
              onClose={() => {
                setBannerDismissed(true)
                sessionStorage.setItem(`sp_profile_done_${currentUser?.email || ''}`, '1')
              }}
            />
          </div>
        )}

        {/* ══ PAGE CONTENT ═══════════════════════════════════ */}
        <main className="ml-content">
          <div className="ml-content-inner">
            <Outlet />
          </div>
        </main>

        {/* ══ FOOTER ═════════════════════════════════════════ */}
        <footer className="ml-footer">
          © {new Date().getFullYear()} Shakthi Power ERP. All Rights Reserved.
        </footer>
      </div>

      {/* ══ MOBILE DRAWER ════════════════════════════════════ */}
      <Drawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        placement="left"
        width={280}
        title={
          <div className="ml-drawer-title">
            <img src={logo} alt="Shakthi Power" className="ml-drawer-logo" />
            <span>Shakthi Power</span>
          </div>
        }
        className="ml-mobile-drawer"
        styles={{ body: { padding: 0 } }}
      >
        {visibleSidebarSections.map(section => {
          const isActive = location.pathname === section.path ||
            (section.children && section.children.some(c => location.pathname === c.key || location.pathname.startsWith(c.key + '/')))
          const go = (key: string) => { navigate(key); setMobileOpen(false) }
          return (
            <div key={section.key} className="ml-drawer-group">
              <button
                className={`ml-drawer-item${isActive && !section.children ? ' active' : ''}`}
                onClick={() => {
                  if (section.path) go(section.path)
                  else if (section.children && section.children.length > 0) go(section.children[0].key)
                }}
              >
                {section.icon} {section.label}
              </button>
              {section.children?.map(child => {
                const childActive = location.pathname === child.key || location.pathname.startsWith(child.key + '/')
                return (
                  <button
                    key={child.key}
                    className={`ml-drawer-item ml-drawer-child${childActive ? ' active' : ''}`}
                    onClick={() => go(child.key)}
                  >
                    {child.icon} {child.label}
                  </button>
                )
              })}
            </div>
          )
        })}
      </Drawer>
    </div>
  )
}

export default MainLayout
